import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, message } from 'antd';
import { Filter, UpdateFilterRequest } from '../../types/api.types';
import { useDataContext } from '../../contexts/DataContext';
import ApiService from '../../services/api';
import { MESSAGES, FILTER_LABELS } from '../../utils/constants';

interface EditFilterModalProps {
  visible: boolean;
  filter: Filter | null;
  filterType?: 'BUY' | 'SELL'; // Добавляем тип фильтра
  fiatName: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditFilterModal: React.FC<EditFilterModalProps> = ({
  visible,
  filter,
  filterType,
  fiatName,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { loadFiatSettings } = useDataContext();

  useEffect(() => {
    if (visible && filter) {
      form.setFieldsValue({
        month_order: filter.month_order,
        month_finish_rate: filter.month_finish_rate,
        max_low_single_trans_amount: filter.max_low_single_trans_amount,
        min_high_single_trans_amount: filter.min_high_single_trans_amount,
        average_size: filter.average_size,
      });
    } else {
      form.resetFields();
    }
  }, [visible, filter, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const updateData: UpdateFilterRequest = {
        month_order: values.month_order,
        month_finish_rate: values.month_finish_rate,
        max_low_single_trans_amount: values.max_low_single_trans_amount,
        min_high_single_trans_amount: values.min_high_single_trans_amount,
        average_size: values.average_size,
      };

      await ApiService.updateFilter(filter!.id, updateData);
      
      message.success(MESSAGES.SUCCESS.FILTER_UPDATED);
      
      // Обновляем данные в кэше
      await loadFiatSettings(fiatName);
      
      onSuccess();
    } catch (error) {
      console.error('Ошибка обновления фильтра:', error);
      message.error(MESSAGES.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const getTitle = () => {
    if (!filter) return 'Редактировать фильтр';
    const typeText = filterType === 'BUY' ? 'Покупка' : 'Продажа';
    return `Редактировать фильтр (${typeText})`;
  };

  return (
    <Modal
      title={getTitle()}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnClose
      width="90%"
      style={{ maxWidth: 600 }}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="month_order"
          label={FILTER_LABELS.month_order}
          rules={[
            { required: true, message: 'Введите значение' },
            { type: 'number', min: 0, message: 'Значение должно быть положительным' },
          ]}
        >
          <InputNumber
            placeholder="Введите значение"
            style={{ width: '100%' }}
            min={0}
            step={1}
          />
        </Form.Item>

        <Form.Item
          name="month_finish_rate"
          label={FILTER_LABELS.month_finish_rate}
          rules={[
            { required: true, message: 'Введите значение' },
            { type: 'number', min: 0, max: 1, message: 'Значение должно быть от 0 до 1' },
          ]}
        >
          <InputNumber
            placeholder="Введите значение 0-1"
            style={{ width: '100%' }}
            min={0}
            max={1}
            step={0.01}
            precision={2}
            addonAfter=""
          />
        </Form.Item>

        <Form.Item
          name="max_low_single_trans_amount"
          label={FILTER_LABELS.max_low_single_trans_amount}
          rules={[
            { required: true, message: 'Введите значение' },
            { type: 'number', min: 0, message: 'Значение должно быть положительным' },
          ]}
        >
          <InputNumber
            placeholder="Введите максимальную сумму начала покупки"
            style={{ width: '100%' }}
            min={0}
            step={1}
          />
        </Form.Item>

        <Form.Item
          name="min_high_single_trans_amount"
          label={FILTER_LABELS.min_high_single_trans_amount}
          rules={[
            { required: true, message: 'Введите значение' },
            { type: 'number', min: 0, message: 'Значение должно быть положительным' },
          ]}
        >
          <InputNumber
            placeholder="Введите минимальную сумму конца покупки"
            style={{ width: '100%' }}
            min={0}
            step={1}
          />
        </Form.Item>

        <Form.Item
          name="average_size"
          label={FILTER_LABELS.average_size}
          rules={[
            { required: true, message: 'Введите значение' },
            { type: 'number', min: 0, message: 'Значение должно быть положительным' },
          ]}
        >
          <InputNumber
            placeholder="Введите средний размер"
            style={{ width: '100%' }}
            min={0}
            step={1}
            precision={0}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditFilterModal;
